import * as React from 'react'
import { ParsedUrlQuery } from 'querystring'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { NextSeo, ArticleJsonLd } from 'next-seo'
import * as Types from '../../declarations'
import { getArticleURL, getUploadThumbPath } from '../../lib/display'
import * as RedisUtil from 'lib/redis'
import PageTemplate from '../../components/PageTemplate'

import ArticlePageComponent from '../../components/articles/ArticlePage'
import { withVersion } from '../../lib/withVersion'
import { getDefaultImage } from 'lib/helper'

interface ArticlePageProps {
	article: Types.DB.Models.ArticleAttributes
	version: Types.VERSION
	trips: Types.App.HomePageTrip[]
}

const ArticlePage = ({ article, version, trips }: ArticlePageProps) => {
	const { isFallback } = useRouter()

	if (isFallback) {
		return <div>Loading...</div>
	}

	const backgroundImage = article.liveJson?.coverImage
		? getUploadThumbPath(article.liveJson.coverImage, 'large')
		: getDefaultImage(version)

	const initialTitle =
		article.title.length > 65
			? article.title.substring(0, 62) + '...'
			: article.title

	const webPageTitle = `${initialTitle} - ${
		version === Types.VERSION.TREKKING ? 'GT' : 'GP'
	}`

	const seo = {
		title: webPageTitle,
		description: article.liveJson.seoDescription,
		canonical: getArticleURL(article as Types.API.Article, version),
		coverImageUrl: backgroundImage,
		siteName:
			version === Types.VERSION.TREKKING ? 'Guided Trekking' : 'Guided Peaks',
		keywords: article?.liveJson?.tags ?? [],
		authorName:
			version === Types.VERSION.TREKKING ? 'Guided Trekking' : 'Guided Peaks',
		datePublished: article?.updatedAt ?? '',
		images: [backgroundImage],
	}

	return (
		<>
			<Head>
				<title>{seo.title}</title>
			</Head>
			<NextSeo
				title={seo.title}
				description={seo.description}
				canonical={seo.canonical}
				openGraph={{
					// 	url: seo.canonical,
					title: seo.title,
					description: seo.description,
					images: seo.coverImageUrl
						? [
								{
									url: seo.coverImageUrl,
									// width: +image.dimensionsLarge.split('x')[0],
									// height: +image.dimensionsLarge.split('x')[1],
									// alt: trip.name,
									type: 'image/jpeg',
								},
						  ]
						: [],
					siteName: seo.siteName,
					type: 'website',
					locale: 'en_US',
				}}
			/>
			<ArticleJsonLd
				title={seo.title}
				keywords={seo.keywords}
				url={seo.canonical}
				description={seo.description}
				authorName={seo.authorName}
				datePublished={seo.datePublished}
				images={seo.images}
			/>
			<PageTemplate isFullWidth={true}>
				<ArticlePageComponent
					article={article}
					imageUrl={backgroundImage}
					version={version}
					trips={trips}
				/>
			</PageTemplate>
		</>
	)
}

interface ArticlePageQuery extends ParsedUrlQuery {
	routeType?: string
	slug?: string
}

// for every possible page, generate a static version
// export const getStaticPaths = withVersion(async (context) => {
// 	const articles = await DataUtil.getArticles(context.version, true)

// 	const paths = articles.map((article) => {
// 		const { slug } = article
// 		return {
// 			params: {
// 				slug,
// 			},
// 		}
// 	})
// 	return { paths, fallback: true }
// })

export const getServerSideProps = withVersion(async (context) => {
	const { slug } = context.params as ArticlePageQuery

	if (!slug) {
		// 404
		return {
			notFound: true,
		}
	}

	const articlePageData = await RedisUtil.Data.Articles.SingleArticle.get({
		slug: slug,
		version: context.version,
	})

	if (!articlePageData) {
		return {
			notFound: true,
		}
	}

	return {
		props: {
			article: articlePageData.article,
			version: articlePageData.version,
			trips: articlePageData.trips,
		},
	}
})

export default ArticlePage
